import { Box, Theme } from "@mui/material";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import withAuth from "../../HOC/withAuth";

import pharmaImage from "../../Assets/images/phamagiant.png";
import databassImage from "../../Assets/images/databass100.png";
import authorcafeImage from "../../Assets/images/authorcafe.png";
import neumaImage from "../../Assets/images/neuma.png";
import project5Image from "../../Assets/images/coding.png";
import project6Image from "../../Assets/images/emotionchat.png";
import zillskillImage from "../../Assets/images/zs_hero_3.png";
import adiHeadshot from "../../Assets/images/adi.jpg";

const preload = new Image();
preload.src = adiHeadshot;

const Home = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://t.contentsquare.net/uxa/bb781a00c0c07.js";
    script.async = true;
    document.head.appendChild(script);
  
    return () => {
      document.head.removeChild(script);
    };
  }, []);
  

  useEffect(() => {
    // Add parallax hover effect to project cards
    const addParallaxEffect = (
      card: HTMLElement,
      isHeroCard: boolean = false
    ) => {
      let bounds: DOMRect;

      const rotateToMouse = (e: MouseEvent) => {
        bounds = card.getBoundingClientRect();
        const mouseX = e.clientX;
        const mouseY = e.clientY;
        const leftX = mouseX - bounds.x;
        const topY = mouseY - bounds.y;
        const center = {
          x: leftX - bounds.width / 2,
          y: topY - bounds.height / 2,
        };

        const rotationDivisorX = isHeroCard ? 160 : 20;
        const rotationDivisorY = isHeroCard ? 80 : 20;
        const scale = isHeroCard ? 1.01 : 1.02;
        const shadowDivisorX = isHeroCard ? 160 : 20;
        const shadowDivisorY = isHeroCard ? 80 : 20;
        const shadowOffset = isHeroCard ? 0.624 : 10;
        const shadowOpacity = isHeroCard ? 0.00936 : 0.08;

        card.style.transform = `
          perspective(500px)
          rotateX(${center.y / rotationDivisorY}deg)
          rotateY(${-center.x / rotationDivisorX}deg)
          scale3d(${scale}, ${scale}, ${scale})
        `;
        card.style.boxShadow = `
          ${center.x / shadowDivisorX}px ${
          center.y / shadowDivisorY + shadowOffset
        }px 30px rgba(0, 0, 0, ${shadowOpacity})
        `;
      };

      const handleMouseEnter = () => {
        bounds = card.getBoundingClientRect();
        document.addEventListener("mousemove", rotateToMouse);
      };

      const handleMouseLeave = () => {
        document.removeEventListener("mousemove", rotateToMouse);
        card.style.transform = "";
        card.style.boxShadow = isHeroCard
          ? "12px 12px 0 #000"
          : "8px 8px 0 #000";
      };

      card.addEventListener("mouseenter", handleMouseEnter);
      card.addEventListener("mouseleave", handleMouseLeave);

      // per-card cleanup
      return () => {
        card.removeEventListener("mouseenter", handleMouseEnter);
        card.removeEventListener("mouseleave", handleMouseLeave);
        document.removeEventListener("mousemove", rotateToMouse);
      };
    };

    // Apply to hero project card with special flag
    const heroCard = document.querySelector(
      ".hero-project-card"
    ) as HTMLElement | null;
    if (heroCard) heroCard.style.boxShadow = "12px 12px 0 #000";
    const heroCleanup = heroCard ? addParallaxEffect(heroCard, true) : null;

    // Apply to all project cards
    const projectCards = document.querySelectorAll(".project-card");
    const cleanups = Array.from(projectCards).map((card) => {
      (card as HTMLElement).style.boxShadow = "8px 8px 0 #000";
      return addParallaxEffect(card as HTMLElement, false);
    });

    // --- Polished custom cursor over "Aditya" ---
    // --- Polished custom cursor with animated sparkline ---
    const addBuildCursor = () => {
      if (matchMedia("(pointer: coarse)").matches) return;

      // Base offsets from the cursor when on the default side (bottom-right)
      const OFFSET_X = 12;
      const OFFSET_Y = 20;

      // Smoothing
      const FOLLOW_POS = 0.25; // tooltip follows cursor
      const FOLLOW_OFF = 0.08; // tooltip offsets tween to the new side

      // Hysteresis buffer near edges (prevents ping-pong)
      const EDGE_BUFFER = 16;

      let cursorEl: HTMLDivElement | null = null;
      let raf = 0;

      // Cursor pos: target vs smoothed
      let targetX = 0,
        targetY = 0;
      let curX = 0,
        curY = 0;

      // Side state + smoothed/tweened offsets
      let sideX: "right" | "left" = "right";
      let sideY: "bottom" | "top" = "bottom";
      let offCurX = OFFSET_X,
        offCurY = OFFSET_Y; // current (animated) offsets
      let offTargetX = OFFSET_X,
        offTargetY = OFFSET_Y; // target offsets (change on flip)

      const pickInitialSide = (
        x: number,
        y: number,
        rectW: number,
        rectH: number,
        vw: number,
        vh: number
      ) => {
        let sx: "right" | "left" = "right";
        let sy: "bottom" | "top" = "bottom";

        // Horizontal
        if (x + OFFSET_X + rectW > vw - EDGE_BUFFER) sx = "left";
        // Vertical
        if (y + OFFSET_Y + rectH > vh - EDGE_BUFFER) sy = "top";

        const offX = sx === "right" ? OFFSET_X : -(rectW + OFFSET_X);
        const offY = sy === "bottom" ? OFFSET_Y : -(rectH + OFFSET_Y);

        return { sx, sy, offX, offY };
      };

      const makeCursor = () => {
        const el = document.createElement("div");
        el.className = "build-cursor";
        el.setAttribute("aria-hidden", "true");
        el.innerHTML = `
            <div class="build-cursor__badge">
              <svg class="sparkline" viewBox="-3 -3 66 26" width="60" height="20" preserveAspectRatio="none">
                <g class="sparkline__g">
                  <path class="sparkline__path" d="" />
                  <circle class="sparkline__glow" r="4.5" cx="0" cy="0" />
                </g>
              </svg>
              <span class="build-cursor__label">Less chaos, more clarity</span>
              <img class="build-cursor__img" alt="Aditya" />
            </div>
          `;
        document.body.appendChild(el);
        return el;
      };

      // Helper: create a randomized, but nice-looking sparkline that ends upward
const buildRandomSparklinePath = (W: number, H: number) => {
  const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

  // Start somewhere in the lower-mid band
  let x = 0;
  let y = H * (0.55 + (Math.random() - 0.5) * 0.2); // ~45–65%
  const pts: Array<{x:number;y:number}> = [{ x, y }];

  // Decide how many "middle" points we want before the final upward finish
  const midCount = Math.floor(Math.random() * 3) + 2; // 2–4 mids
  const finishStartX = W * (0.60 + Math.random() * 0.10); // start the final climb ~60–70% width

  // Build middle segments with gentle random walk (bounded)
  for (let i = 1; i <= midCount; i++) {
    // Spread mids up to finishStartX
    const t = i / (midCount + 1);
    x = (finishStartX * t) + (W * 0.02 * (Math.random() - 0.5)); // slight jitter
    const dy = (Math.random() - 0.5) * (H * 0.25);               // bounded vertical wiggle
    y = clamp(y + dy, H * 0.15, H * 0.90);                       // keep inside chart
    pts.push({ x, y });
  }

  // Now force an upward finish:
  // Choose a pre-finish point (slightly below final) and the final high point.
  const preFinishX = W * (0.85 + Math.random() * 0.07);          // 85–92% width
  const finalX = W;
  const finalY = H * (0.08 + Math.random() * 0.10);              // end high (8–18% from top)
  // Make pre-finish lower than final so last leg is clearly rising
  const preFinishY = clamp(finalY + H * (0.12 + Math.random() * 0.15), H * 0.20, H * 0.85);

  pts.push({ x: preFinishX, y: preFinishY });
  pts.push({ x: finalX, y: finalY });

  // Build "M … L …" path string
  const d = pts.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(" ");
  return d;
};

const playSparkline = (svg: SVGSVGElement) => {
  const path = svg.querySelector(".sparkline__path") as SVGPathElement | null;
  const dot  = svg.querySelector(".sparkline__glow") as SVGCircleElement | null;
  if (!path || !dot) return;

  const W = 60, H = 20;

  // New randomized path each time (always ends upward)
  const d = buildRandomSparklinePath(W, H);
  path.setAttribute("d", d);

  // (Re)measure after setting d
  const L = path.getTotalLength();

  // Reset dash for draw animation
  path.style.strokeDasharray = `${L}`;
  path.style.strokeDashoffset = `${L}`;

  // Randomize duration a little for variety, but keep it snappy
  const dur = 1600 + Math.floor(Math.random() * 600); // 1600–2200ms
  const t0 = performance.now();

  // Slight ease-out on stroke reveal
  const easeOutCubic = (x: number) => 1 - Math.pow(1 - x, 3);

  const step = (now: number) => {
    const k = Math.min(1, (now - t0) / dur);
    const e = easeOutCubic(k);
    path.style.strokeDashoffset = `${L * (1 - e)}`;

    // Move the glow dot along the visible path
    const pt = path.getPointAtLength(L * e);
    dot.setAttribute("cx", `${pt.x}`);
    dot.setAttribute("cy", `${pt.y}`);

    if (k < 1) requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
};


      const ensureCursor = () => (cursorEl ??= makeCursor());

      // Safely find a highlight element even if e.target is a text node
      const getHighlightTarget = (e: Event) => {
        const node = e.target as Node;
        return node instanceof Element
          ? node.closest(".philosophy-highlight")
          : null;
      };

      const loop = () => {
        // Smooth follow toward cursor
        curX += (targetX - curX) * FOLLOW_POS;
        curY += (targetY - curY) * FOLLOW_POS;

        if (cursorEl) {
          const rect = cursorEl.getBoundingClientRect();
          const vw = window.innerWidth;
          const vh = window.innerHeight;

          // Predict placement using CURRENT (smoothed) offsets
          let desiredX = curX + offCurX;
          let desiredY = curY + offCurY;

          // Decide if we should flip sides (with hysteresis buffer)
          // Horizontal
          if (sideX === "right") {
            if (desiredX + rect.width > vw - EDGE_BUFFER) {
              sideX = "left";
              offTargetX = -(rect.width + OFFSET_X); // target to left (smoothly)
            } else {
              offTargetX = OFFSET_X; // stay on right as target
            }
          } else {
            // left
            if (curX + OFFSET_X + rect.width < vw - EDGE_BUFFER) {
              sideX = "right";
              offTargetX = OFFSET_X;
            } else {
              offTargetX = -(rect.width + OFFSET_X);
            }
          }

          // Vertical
          if (sideY === "bottom") {
            if (desiredY + rect.height > vh - EDGE_BUFFER) {
              sideY = "top";
              offTargetY = -(rect.height + OFFSET_Y); // target above (smoothly)
            } else {
              offTargetY = OFFSET_Y; // stay bottom
            }
          } else {
            // top
            if (curY + OFFSET_Y + rect.height < vh - EDGE_BUFFER) {
              sideY = "bottom";
              offTargetY = OFFSET_Y;
            } else {
              offTargetY = -(rect.height + OFFSET_Y);
            }
          }

          // Smooth offsets toward their targets (this makes the flip *animated*)
          offCurX += (offTargetX - offCurX) * FOLLOW_OFF;
          offCurY += (offTargetY - offCurY) * FOLLOW_OFF;

          // Final position using smoothed offsets
          desiredX = curX + offCurX;
          desiredY = curY + offCurY;

          cursorEl.style.left = `${desiredX}px`;
          cursorEl.style.top = `${desiredY}px`;
        }

        raf = requestAnimationFrame(loop);
      };

      const onEnter = (e: Event) => {
        const t = getHighlightTarget(e);
        if (!t) return;

        const el = ensureCursor();
        const label = el.querySelector(
          ".build-cursor__label"
        ) as HTMLSpanElement | null;
        const img = el.querySelector(
          ".build-cursor__img"
        ) as HTMLImageElement | null;
        const svg = el.querySelector("svg.sparkline") as SVGSVGElement | null;

        const isAditya = (t as HTMLElement).classList.contains("aditya-name");
        const imgSrc = t.getAttribute("data-tooltip-img");

        // Configure content BEFORE measuring
        if (isAditya && img && imgSrc) {
          img.src = imgSrc;
          img.style.display = "block";
          if (label) label.style.display = "none";
          if (svg) svg.style.display = "none";
          el.querySelector(".build-cursor__badge")?.setAttribute(
            "style",
            "background:none; border:none; box-shadow:none; padding:0;"
          );
        } else {
          if (label) {
            label.textContent =
              t.getAttribute("data-tooltip") || "Less chaos, more clarity";
            label.style.display = "inline";
          }
          if (svg) svg.style.display = "block";
          if (img) img.style.display = "none";
          el.querySelector(".build-cursor__badge")?.removeAttribute("style");
        }

        // Mouse position (target & current start here)
        const me = e as MouseEvent;
        targetX = curX = me.clientX;
        targetY = curY = me.clientY;

        // ---- PRE-MEASURE & PRE-PICK SIDE (no flicker) ----
        // Temporarily make it measurable but invisible
        const prevOpacity = el.style.opacity;
        const prevTransform = el.style.transform;

        el.style.opacity = "0";
        el.style.transform = "scale(1)";
        el.style.visibility = "hidden"; // hide without affecting layout
        // position it near cursor so size is accurate with current content
        el.style.left = `${targetX + OFFSET_X}px`;
        el.style.top = `${targetY + OFFSET_Y}px`;

        // Force reflow to ensure correct measurements
        const rect = el.getBoundingClientRect();
        const { sx, sy, offX, offY } = pickInitialSide(
          targetX,
          targetY,
          rect.width,
          rect.height,
          window.innerWidth,
          window.innerHeight
        );

        sideX = sx;
        sideY = sy;
        offTargetX = offCurX = offX;
        offTargetY = offCurY = offY;

        // Position to the precomputed side *before* first visible frame
        el.style.left = `${targetX + offCurX}px`;
        el.style.top = `${targetY + offCurY}px`;

        // Restore visibility for the reveal
        el.style.visibility = "";
        el.style.opacity = "1";
        el.style.transform = "scale(1)";

        // Optional: restore stored values (not strictly needed)
        // (kept for completeness in case you had transitions tied to them)
        void prevOpacity;
        void prevTransform;

        // Only animate sparkline when NOT in image mode
        if (!imgSrc && svg) playSparkline(svg);

        loop();
      };

      const onLeave = (e: Event) => {
        const t = getHighlightTarget(e);
        if (!t) return;
        cancelAnimationFrame(raf);
        if (cursorEl) {
          cursorEl.style.opacity = "0";
          cursorEl.style.transform = "scale(.96)";
        }
      };

      const onMove = (e: MouseEvent) => {
        const t = getHighlightTarget(e);
        if (!t) return;
        targetX = e.clientX;
        targetY = e.clientY;
      };

      document.addEventListener("pointerenter", onEnter, true);
      document.addEventListener("pointerleave", onLeave, true);
      document.addEventListener("pointermove", onMove, true);

      return () => {
        document.removeEventListener("pointerenter", onEnter, true);
        document.removeEventListener("pointerleave", onLeave, true);
        document.removeEventListener("pointermove", onMove, true);
        cancelAnimationFrame(raf);
        if (cursorEl) cursorEl.remove();
      };
    };

    const buildCursorCleanup = addBuildCursor(); // << create it BEFORE return

    // Cleanup on unmount (run all the cleanups)
    return () => {
      if (heroCleanup) heroCleanup();
      cleanups.forEach((c) => c());
      if (buildCursorCleanup) buildCursorCleanup(); // << include here
    };
  }, []);

  // replace your existing handler with this
  const handleProjectClick = (link: string) => {
    const isExternal = /^https?:\/\//i.test(link);
    if (isExternal) {
      window.open(link, "_blank", "noopener,noreferrer");
    } else {
      navigate(link);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", overflow: "hidden" }}>
      {/* Blue Circle Page-Load Animation (centered, “sticky”, elegant) */}
      {/* Blue Circle Page-Load Animation */}
      <Box
        sx={{
          position: "fixed",
          width: "1px",
          height: "1px",
          background: (theme: Theme) => theme.palette.primary.main,
          top: 0,
          left: 0,
          zIndex: 9999999999,
          borderRadius: "100%",
          animation: "animateCircle1 0.7s ease-out",
          animationIterationCount: 1,
          "@keyframes animateCircle1": {
            from: { transform: "scale(4000)" },
            to: { transform: "scale(0)" },
          },
        }}
      />

      <style>
        {`
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          
          /* Add these styles inside your existing <style> block */
          .project-image {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;  /* Changed from cover to contain */
            object-position: center center;
            display: block;
        }
          
          /* Hero Project Image */
          .hero-project-image-container {
              background: #fafafa;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              padding: 20px;  /* Add padding for breathing room */
              height: 100%;
              min-height: 300px;
          }
          
          .hero-project-image-container img {
              max-width: 100%;
              max-height: 100%;
              width: auto;
              height: auto;
              object-fit: contain;  /* Changed from cover to contain */
              object-position: center center;
              margin: 0;
              padding: 0;
              display: block;
          }

          body {
            font-family: 'DM Sans', sans-serif;
            background: #ffffff;
            color: #333;
            overflow-x: hidden;
          }
          
          @media (max-width: 768px) {
            .philosophy-highlight svg {
              height: 15px;
              bottom: -2px;
            }
            
            .philosophy-highlight::after {
              height: 15px;
            }
          }

          /* Hero Section */
          .hero-section {
            min-height: 100vh;
            padding: 80px 60px;
            max-width: 1400px;
            margin: 0 auto;
            display: flex;
            flex-direction: column;
            justify-content: center;
            position: relative;
          }
          
          /* Intro Section */
          .intro-section {
            margin-bottom: 120px;
          }
          
          
          
          .name-title {
            font-size: clamp(36px, 4vw, 42px);  /* down from 40–60px */
            font-weight: 600;
            margin-bottom: 20px;
            letter-spacing: -1px;
          }
          
          
          .name-title strong {
            font-weight: 900;
            color: #0021F5;
          }
          
          .design-philosophy {
            font-size: clamp(22px, 2.2vw, 260px);  /* up from 18–22px */
            font-weight: 400;
            line-height: 1.5;
            max-width: 1100px;  /* keep your wider container */
            margin-bottom: 40px;
            color: #333;
          }
          
          
          
          .philosophy-highlight {
            position: relative;
            display: inline-block;
            color: #0021F5;
            font-weight: 600;    /* just a little bolder */
            cursor: pointer;
            text-decoration: none;
            line-height: 1.4;
          }
          
          .philosophy-highlight::after {
            content: "";
            position: absolute;
            left: 0;
            right: 0;
            bottom: 0;              /* sits right at text baseline */
            height: 2px;            /* thinner underline */
            background: currentColor; /* same blue as the text */
            border-radius: 1px;
          }
          
          /* OPTIONAL: If you want a subtle hover ‘tighten’ without animation restarts */
          @media (hover:hover) {
            .philosophy-highlight:hover::after {
              height: 3px;               /* keep same height for consistency */
              filter: brightness(1.05);  /* tiny pop */
            }
          }


          /* one-time reveal on page load; leaves dashoffset at 0 (visible) */
          @keyframes drawOnce {
            to { stroke-dashoffset: 0; }
          }

          @keyframes drawZigzag {
            to {
              stroke-dashoffset: 0;
            }
          }
          
          @keyframes underlineReveal {
            to {
              transform: scaleX(1);
            }
          }
          
          .bio-pills {
            display: flex;
            gap: 20px;
            flex-wrap: wrap;
          }
          
          .bio-pill {
            padding: 20px 30px;
            background: #f5f5f5;
            border: 4px solid #000;
            box-shadow: 5px 5px 0 #000;
            font-size: 18px;
            font-weight: 900;
            text-transform: uppercase;
            display: flex;
            align-items: center;
            gap: 12px;
            position: relative;
            cursor: default;
          }
          
          .bio-pill::after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: repeating-linear-gradient(
              45deg,
              transparent,
              transparent 10px,
              rgba(0,0,0,0.03) 10px,
              rgba(0,0,0,0.03) 20px
            );
            pointer-events: none;
          }
          
          .bio-pill span {
            font-size: 24px;
          }
          
          /* Impact Section */
          .impact-section {
            margin-bottom: 60px;
          }
          
          .impact-header {
            display: flex;
            align-items: baseline;
            gap: 20px;
            margin-bottom: 40px;
          }
          
          .impact-title {
            font-size: clamp(60px, 8vw, 100px);
            font-weight: 900;
            text-transform: uppercase;
            color: #0021F5;
            letter-spacing: -4px;
            line-height: 0.9;
          }
          
          /* Impact Metrics Grid */
          .impact-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 30px;
            margin-bottom: 80px;
          }
          
          .impact-card {
            background: #0021F5;
            color: white;
            padding: 40px;
            position: relative;
            overflow: hidden;
          }
          
          .impact-card:nth-child(1) { animation-delay: 1s; }
          .impact-card:nth-child(2) { animation-delay: 1.1s; }
          .impact-card:nth-child(3) { animation-delay: 1.2s; }
          .impact-card:nth-child(4) { animation-delay: 1.3s; }
          
          .impact-card::before {
            content: '';
            position: absolute;
            top: -50%;
            right: -50%;
            width: 200%;
            height: 200%;
            background: radial-gradient(circle, rgba(173,255,0,0.3) 0%, transparent 70%);
            animation: pulse 3s ease-in-out infinite;
          }
          
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.1); opacity: 0.3; }
          }
          
          .impact-number {
            font-size: clamp(48px, 6vw, 72px);
            font-weight: 900;
            margin-bottom: 10px;
            color: #ADFF00;
            text-shadow: 2px 2px 0 rgba(0,0,0,0.1);
          }
          
          .impact-label {
            font-size: 18px;
            font-weight: 600;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          
          .impact-context {
            font-size: 14px;
            margin-top: 5px;
          }
          
          /* Projects Preview Section */
        
          
          .projects-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 40px;
          }
          
          .projects-title {
            font-size: 24px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          
          .view-all-link {
            color: #0021F5;
            text-decoration: none;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: gap 0.3s ease;
            cursor: pointer;
          }
          
          .view-all-link:hover {
            gap: 12px;
          }
          
          /* Hero Project Card */
          .hero-project-card {
            background: white;
            border: 4px solid #000;
            cursor: pointer;
            transition: transform 0.15s ease-out, box-shadow 0.15s ease-out;
            position: relative;
            margin-bottom: 70px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            min-height: 400px;
            transform-style: preserve-3d;
            will-change: transform;
            z-index: 1;
            box-shadow: 12px 12px 0 #000;
            margin-right: 12px;
          }
          
          .hero-project-card:hover {
            transform: translate(-4px, -4px);
            box-shadow: 16px 16px 0 #000;
            z-index: 10;
          }
          
          .hero-project-image {
            background: #f5f5f5;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 120px;
            color: #ddd;
          }
          
          .hero-project-content {
            padding: 60px;
            display: flex;
            flex-direction: column;
            justify-content: center;
          }
          
          .hero-project-tag {
            display: inline-block;
            padding: 8px 20px;
            background: #0021F5;
            color: white;
            font-size: 14px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 24px;
            width: fit-content;
          }
          
          .hero-project-name {
            font-size: 36px;
            font-weight: 800;
            margin-bottom: 20px;
            text-transform: uppercase;
            letter-spacing: -0.5px;
            line-height: 1.1;
          }
          
          .hero-project-question {
            font-size: 28px;
            font-weight: 600;
            margin-bottom: 30px;
            color: #0021F5;
            line-height: 1.3;
          }
          
          .hero-project-impact-label {
            font-size: 14px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #666;
            margin-bottom: 10px;
          }
          
          .hero-project-impact-text {
            font-size: 20px;
            color: #333;
            line-height: 1.5;
            margin-bottom: 36px;
          }
          
          .hero-view-case-study {
            color: #0021F5;
            font-weight: 700;
            font-size: 18px;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 10px;
            transition: gap 0.3s ease;
          }
          
          .hero-view-case-study:hover {
            gap: 15px;
          }
          
          /* Regular Project Cards Grid */
          .projects-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
            gap: 40px;
            width: 100%;
            padding: 10px;
          }
          
          .project-card {
            background: white;
            border: 4px solid #000;
            cursor: pointer;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            will-change: transform;
            box-shadow: 8px 8px 0 #000;
            position: relative;
            margin-bottom: 10px;
            margin-right: 10px;
          }
          
          .project-card:hover {
            transform: translate(-4px, -4px);
            box-shadow: 12px 12px 0 #000;
          }
          
          /* Regular Project Images */
          .project-image-container {
              width: 100%;
              height: 333px;
              background: #fafafa;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              padding: 20px;  /* Add padding for breathing room */
          }
          
          .project-image-placeholder {
            width: 100%;
            height: 250px;
            background: #f5f5f5;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 60px;
            color: #ddd;
          }
          
          .project-content {
            padding: 30px;
          }
          
          .project-name {
            font-size: 18px;
            font-weight: 800;
            margin-bottom: 12px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #333;
          }
          
          .project-question {
            font-size: 20px;
            font-weight: 600;
            margin-bottom: 20px;
            color: #0021F5;
            line-height: 1.3;
          }
          
          .project-impact-label {
            font-size: 14px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #666;
            margin-bottom: 8px;
          }
          
          .project-impact-text {
            font-size: 16px;
            color: #333;
            line-height: 1.5;
            margin-bottom: 24px;
          }
          
          .view-case-study {
            color: #0021F5;
            font-weight: 600;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            transition: gap 0.3s ease;
          }
          
          .view-case-study:hover {
            gap: 12px;
          }
          
          /* Responsive */
          @media (max-width: 768px) {
            .hero-section {
              padding: 40px 20px;
              min-height: auto;
            }
            
            .intro-section {
              margin-bottom: 60px;
            }
            
            .name-title {
              font-size: clamp(32px, 8vw, 48px);
            }
            
            .design-philosophy {
              font-size: clamp(20px, 5vw, 28px);
            }
            
            .bio-pills {
              flex-direction: column;
              gap: 15px;
            }
            
            .bio-pill {
              padding: 12px 20px;
              font-size: 16px;
              justify-content: center;
            }
            
            .impact-header {
              flex-direction: column;
              gap: 10px;
              align-items: flex-start;
            }
            
            .impact-title {
              font-size: clamp(48px, 12vw, 72px);
            }
            
            .impact-grid {
              grid-template-columns: 1fr;
              gap: 20px;
            }
            
            .impact-card {
              padding: 30px;
            }
            
            .impact-number {
              font-size: clamp(40px, 10vw, 56px);
            }
            
            .projects-header {
              flex-direction: column;
              gap: 15px;
              align-items: flex-start;
            }
            
            .projects-grid {
              grid-template-columns: 1fr;
              gap: 30px;
              padding: 5px;
            }
            
            .project-card {
              margin-right: 5px;
              margin-bottom: 5px;
            }
            
            .project-card:hover {
              transform: translate(-2px, -2px);
              box-shadow: 10px 10px 0 #000;
            }
            
            .hero-project-card {
              grid-template-columns: 1fr;
              min-height: auto;
              margin-right: 5px;
              box-shadow: 8px 8px 0 #0021F5;
            }
            
            .hero-project-card:hover {
              transform: translate(-2px, -2px);
              box-shadow: 10px 10px 0 #0021F5;
            }
            
            .hero-project-content {
              padding: 30px 20px;
            }
            
            .hero-project-name {
              font-size: 24px;
            }
            
            .hero-project-question {
              font-size: 18px;
            }
            
            .hero-project-image-container {
              height: 250px;
              padding: 15px;
            }
            
            .project-image-container {
              height: 200px;
              padding: 15px;
            }
            
            .project-content {
              padding: 20px;
            }
            
            .project-name {
              font-size: 16px;
            }
            
            .project-question {
              font-size: 18px;
            }
          }
          
          /* Tablet styles */
          @media (min-width: 769px) and (max-width: 1024px) {
            .hero-section {
              padding: 60px 40px;
            }
            
            .impact-grid {
              grid-template-columns: repeat(2, 1fr);
            }
            
            .hero-project-card {
              grid-template-columns: 1fr;
            }
          }
          
          /* Small screens */
          @media (max-width: 480px) {
            .hero-section {
              padding: 30px 15px;
            }
            
            .name-title {
              font-size: 28px;
            }
            
            .design-philosophy {
              font-size: 18px;
              line-height: 1.4;
            }
            
            .bio-pill {
              padding: 10px 15px;
              font-size: 14px;
              box-shadow: 3px 3px 0 #000;
            }
            
            .impact-card {
              padding: 25px 20px;
            }
            
            .projects-title {
              font-size: 20px;
            }
          }
          
          /* Arrow */
          .arrow {
            display: inline-block;
            width: 16px;
            height: 16px;
            border-top: 2px solid currentColor;
            border-right: 2px solid currentColor;
            transform: rotate(45deg);
            transition: transform 0.3s ease;
          }




/* Cursor container */
.build-cursor {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  pointer-events: none;
  opacity: 0;
  transform: scale(.96);        /* no translate */
  transform-origin: top left;   /* anchor from top-left */
  transition: opacity .18s ease, transform .18s ease;
}

.build-cursor__img {
  display: none;               /* stays hidden until Aditya hover */
  width: clamp(180px, 28vw, 320px);
  aspect-ratio: 1 / 1;         /* perfect square without forcing height */
  object-fit: cover;
  border-radius: 12px;          /* tweak to taste (8px/50% for circle) */
  box-shadow: 0 12px 28px rgba(0,0,0,0.28);
  will-change: transform;
}



/* Sleek pill */
.build-cursor__badge{
  display:inline-flex; align-items:center; gap:18px;
  padding:20px 28px; border-radius:999px;
  background:linear-gradient(180deg,#111 0%, #1a1a1a 100%);
  border:1px solid rgba(255,255,255,.08);
  box-shadow:0 8px 24px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.08);
  color:#fff; font:600 14px/1 'DM Sans',sans-serif;
  letter-spacing:.05em; text-transform:uppercase; white-space:nowrap;
}

/* Sparkline container (fixed size for crispness) */
.sparkline{
  width:36px; height:14px; display:block;
}

/* Sparkline path + glow */
.sparkline__path{
  fill:none; stroke:#ADFF00; stroke-width:3; stroke-linecap:round; opacity:.95;
  filter: drop-shadow(0 0 2px rgba(173,255,0,.6));
}
.sparkline__glow{
  fill:#ADFF00; opacity:.95; filter: drop-shadow(0 0 4px rgba(173,255,0,.9));
}

/* Reduced motion */
@media (prefers-reduced-motion: reduce){
  .build-cursor{ transition:opacity .18s ease; transform:translate(-50%,-50%); }
}
.build-cursor__badge { overflow: visible; }   /* let SVG glow spill outside */
.sparkline { overflow: visible; display: block; }

          
.project-tag {
  display: inline-block;
  padding: 4px 12px;
  background: #0021F5;
  color: white;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.project-name-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
}
/* Remove underline-only for the Aditya name */
.aditya-name::after { 
  content: none !important; 
  display: none !important; 
}

/* (Optional) also prevent hover tweaks from re-adding anything */
.aditya-name:hover::after {
  content: none !important;
  display: none !important;
}

/* (Optional) if you want Aditya to use normal text color instead of the blue highlight */
.aditya-name {
  color: inherit;           /* or set to your preferred color */
  font-weight: 900;         /* keep the bold look if you want */
}

/* --- HOVER PRESS + SEAMLESS HATCH (non-clickable) --- */
/* Keep your current dimensions & typography exactly as-is */
.bio-pill {
  /* you already set: padding: 15px 30px; font-size: 18px; */
  /* keep height stable by NOT scaling on hover */
  transition: transform .18s ease, box-shadow .18s ease;
  will-change: transform, box-shadow;
  cursor: default;           /* not “clickable” */
  user-select: none;
  /* local vars so we don’t touch your typography */
  --hatch-tile: 20px;
  --hatch-alpha: 0.03;       /* same visual weight as your previous look */
}

/* Keep overlay purely visual; it never affects layout */
.bio-pill::after {
  /* replace the repeating-linear-gradient with a tileable stripe pattern */
  background-image: linear-gradient(
    45deg,
    rgba(0,0,0,var(--hatch-alpha)) 0 25%,
    transparent 25% 50%,
    rgba(0,0,0,var(--hatch-alpha)) 50% 75%,
    transparent 75% 100%
  );
  background-size: var(--hatch-tile) var(--hatch-tile);  /* exact tile */
  background-repeat: repeat;
  background-position: 0 0;
  /* make sure it sits above content but doesn’t shift layout */
  z-index: 0;
}

/* Press effect: translate only (no scale) so box height is unchanged */
.bio-pill:hover {
  transform: translate(6px, 6px);  /* slides “into” its shadow */
  box-shadow: 0 0 0 #000;          /* shadow collapses visually */
  /* avoid filter/saturate so hatch doesn’t look darker */
}

/* Run the stripes only while hovering */
.bio-pill:hover::after {
  animation: pillStripes .6s linear infinite;
}

/* Move exactly one tile width for a perfect wrap */
@keyframes pillStripes {
  from { background-position: 0 0; }
  to   { background-position: calc(-1 * var(--hatch-tile)) 0; }
}

/* Smooth transition */
.philosophy-highlight {
  transition: color .18s ease;
}

/* Hover: switch from blue to dark gray (#333) */
@media (hover: hover) {
  .philosophy-highlight:hover {
    color: #333;
  }
  .philosophy-highlight:hover::after {
    background: currentColor; /* underline follows */
  }
}

/* Keyboard focus (accessibility) */
.philosophy-highlight:focus-visible {
  color: #333;
  outline: none;
  text-decoration: none;
}
.philosophy-highlight:focus-visible::after {
  background: currentColor;
}

        `}
      </style>

      {/* Hero Section */}
      <section className="hero-section">
        {/* Introduction */}
        <div className="intro-section">
          <h1 className="name-title">
            Hi, I'm{" "}
            <strong
              className="aditya-name philosophy-highlight"
              data-tooltip-img={adiHeadshot}
            >
              Aditya
            </strong>
          </h1>

          <p className="design-philosophy">
          I'd rather rewrite the problem than polish the wrong screen.          </p>
          <p className="design-philosophy" style={{ marginTop: '-20px' }}>
            Recent work:{" "}
            <span
              className="philosophy-highlight"
              data-tooltip="85% faster workflows for 400+ Johnson & Johnson analysts"
              role="link"
              tabIndex={0}
              onClick={() => handleProjectClick("/pharmagiant")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleProjectClick("/pharmagiant");
                }
              }}
              aria-label="Open the American Pharma Giant case study"
            >
              B2B Enterprise
            </span>{" "}
            |{" "}
            <span
              className="philosophy-highlight"
              data-tooltip="Databass AI scaled to 100K+ global users"
              role="link"
              tabIndex={0}
              onClick={() => handleProjectClick("/databassai")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleProjectClick("/databassai");
                }
              }}
              aria-label="Open the Databass AI case study"
            >
              AI Music Product
            </span>
          </p>

          <div className="bio-pills">
            <div className="bio-pill">
              Sr. Product Designer @ J.P. Morgan Private Bank
            </div>
            <div className="bio-pill">
              5+ years designing products
            </div>
          </div>
        </div>

        {/* Projects Preview */}
        <div className="projects-preview">
          <div className="projects-header">
            <h2 className="impact-title">Work</h2>
          </div>

          {/* Hero Project - J&J */}
          <div
            className="hero-project-card"
            onClick={() => handleProjectClick("/pharmagiant")}
          >
            <div className="hero-project-image-container">
              <img src={pharmaImage} alt="Johnson & Johnson Supply Chain Project" />
            </div>
            <div className="hero-project-content">
              <span className="hero-project-tag">Enterprise</span>
              <h4 className="hero-project-name">J&J</h4>
              <p className="hero-project-question">
                How might we simplify complex supply chain reporting at scale?
              </p>
              <p className="hero-project-impact-label">Impact</p>
              <p className="hero-project-impact-text">
                Reduced task completion time by 85% (from 30 to 5 minutes) for
                a Fortune 50 company's global supply chain operations.
              </p>
              <a className="hero-view-case-study">
                View Case Study <span className="arrow"></span>
              </a>
            </div>
          </div>

          {/* Other Projects Grid */}
          <div className="projects-grid">
            <div
              className="project-card"
              onClick={() => handleProjectClick("/databassai")}
            >
              <div className="project-image-container">
                <img
                  src={databassImage}
                  alt="Databass AI Project"
                  className="project-image"
                />
              </div>
              <div className="project-content">
                <div className="project-name-row">
                  <h4 className="project-name">DATABASS AI</h4>
                  <span className="project-tag">Consumer AI</span>
                </div>
                <p className="project-question">
                  How might we guide AI to interpret genres the way each user
                  means them?
                </p>
                <p className="project-impact-label">Impact</p>
                <p className="project-impact-text">
                  Scaled to 100K+ users and turned hit-or-miss generation into
                  personalized, accurate outputs.
                </p>
                <a className="view-case-study">
                  View Case Study <span className="arrow"></span>
                </a>
              </div>
            </div>

            <div
              className="project-card"
              onClick={() => handleProjectClick("/zillskill")}
            >
              <div className="project-image-container">
                <img
                  src={zillskillImage}
                  alt="ZillSkill"
                  className="project-image"
                />
              </div>
              <div className="project-content">
                <div className="project-name-row">
                  <h4 className="project-name">ZILLSKILL</h4>
                  <span className="project-tag">ED-TECH</span>
                </div>{" "}
                <p className="project-question">
                  How might we make hobby discovery engaging and sustainable for
                  kids?
                </p>
                <p className="project-impact-label">Impact</p>
                <p className="project-impact-text">
                  Designed a platform end-to-end that helps kids explore and
                  stick with hobbies using a psychologist-backed test, daily
                  challenges, and dashboards for kids, parents, and tutors.
                </p>
                <a className="view-case-study">
                  View Case Study <span className="arrow"></span>
                </a>
              </div>
            </div>

            <div
              className="project-card"
              onClick={() => handleProjectClick("/authorcafe")}
            >
              <div className="project-image-container">
                <img
                  src={authorcafeImage}
                  alt="AuthorCafé Project"
                  className="project-image"
                />
              </div>
              <div className="project-content">
                <div className="project-name-row">
                  <h4 className="project-name">AUTHORCAFÉ</h4>
                  <span className="project-tag">B2C SaaS</span>
                </div>
                <p className="project-question">
                  How might we make research writing software intuitive instead
                  of overwhelming?
                </p>
                <p className="project-impact-label">Impact</p>
                <p className="project-impact-text">
                  Achieved 80% improvement in user satisfaction through
                  evidence-based redesign validated across 3 rounds of usability
                  testing.
                </p>
                <a className="view-case-study">
                  View Case Study <span className="arrow"></span>
                </a>
              </div>
            </div>

            <div
              className="project-card"
              onClick={() => handleProjectClick("/neuma")}
            >
              <div className="project-image-container">
                <img
                  src={neumaImage}
                  alt="Neuma Smart Mattress Project"
                  className="project-image"
                />
              </div>
              <div className="project-content">
                <div className="project-name-row">
                  <h4 className="project-name">NEUMA</h4>
                  <span className="project-tag">CONSUMER APP</span>
                </div>{" "}
                <p className="project-question">
                  How might we create a mobile experience that makes controlling
                  a smart mattress feel intuitive and premium for India's first
                  IoT sleep product?
                </p>
                <p className="project-impact-label">Impact</p>
                <p className="project-impact-text">
                  Designed and launched India's first smart mattress controller,
                  achieving user preference through rigorous A/B testing.
                </p>
                <a className="view-case-study">
                  View Case Study <span className="arrow"></span>
                </a>
              </div>
            </div>

            {/* <div
              className="project-card"
              onClick={() => handleProjectClick("/creativecoding")}
            >
              <div className="project-image-container">
                <img
                  src={project5Image}
                  alt="Creative Coding"
                  className="project-image"
                />
              </div>
              <div className="project-content">
                <h4 className="project-name">CREATIVE CODING</h4>
                <p className="project-question">Fun with p5.js</p>
                <p className="project-impact-label">Impact</p>
                <p className="project-impact-text">
                  🤔 Keeps me creatively occupied
                </p>
                <a className="view-case-study">
                  View Projects <span className="arrow"></span>
                </a>
              </div>
            </div> */}

            {/* <div
              className="project-card"
              onClick={() => handleProjectClick("/emotionalchat")}
            >
              <div className="project-image-container">
                <img
                  src={project6Image}
                  alt="Experimental emotion-sensitive chat"
                  className="project-image"
                />
              </div>
              <div className="project-content">
                  <h4 className="project-name">EMOTION-SENSITIVE CHAT PLATFORM</h4>
                  
                <p className="project-question">
                  An experimental chat platform that rephrases your sent
                  messages based on your live facial expressions
                </p>
                <p className="project-impact-label">Impact</p>
                <p className="project-impact-text">
                  Makes textual conversations more aware of your emotions, so
                  they are actually reflective of your current mental state
                </p>
                <a className="view-case-study">
                  View Project <span className="arrow"></span>
                </a>
              </div>
            </div> */}

          </div>
        </div>

        {/* Impact Section */}
        <div className="impact-section" style={{ marginTop: '120px' }}>
          <div className="impact-header">
            <h2 className="impact-title">Impact</h2>
          </div>

          <div className="impact-grid">
            <div className="impact-card">
              <div className="impact-number">6X</div>
              <div className="impact-label">EFFICIENCY GAIN</div>
              <div className="impact-context">
                Fortune 50 brand transformation
              </div>
            </div>

            <div className="impact-card" style={{ background: "#FF1E1E" }}>
              <div className="impact-number">100K+</div>
              <div className="impact-label">AI PRODUCT LAUNCH</div>
              <div className="impact-context">Users acquired; Databass AI</div>
            </div>

            <div className="impact-card" style={{ background: "#333" }}>
              <div className="impact-number">30M+</div>
              <div className="impact-label">USERS IMPACTED</div>
              <div className="impact-context">
                Mobile app onboarding at scale
              </div>
            </div>

            <div className="impact-card" style={{ background: "#7C3AED" }}>
              <div className="impact-number">120+</div>
              <div className="impact-label">PEOPLE MENTORED</div>
              <div className="impact-context">Teaching & Leadership</div>
            </div>
          </div>
        </div>
      </section>
    </Box>
  );
};

export default withAuth(Home);